<template>
  <SliderModalContainer
    teleport-to=".swa-mweb-filters-modal-container"
    :modal-title="$t('products.filters.title')"
    modal-header-animation="slide"
    modal-close-btn-icon="close"
    :modal-clear-btn-text="
      toShowClearBtn ? $t('products.filters.clearAll') : ''
    "
    @clear="clearAll()"
    @default-opened="handleOpened"
    @default-closed="handleClosed"
  >
    <template #slider-modal-trigger-btn>
      <span class="mweb-trigger-filter-btn">
        {{ $t('products.filter') }}
      </span>
    </template>

    <template #slider-modal-content>
      <CategoryFilters
        v-if="filterData.categoryFilters.length"
        :category-filters="filterData.categoryFilters"
      />
      <BrandFilters
        v-if="filterData.brandFilters.length"
        :brand-filters="filterData.brandFilters"
      />
      <PriceFilters
        :price-range-filter="filterData.priceRangeFilter"
        :to-reset-price-range="toResetPriceRange"
        @range-resetted="toResetPriceRange = false"
      />
      <ProductTypeFilters
        v-if="filterData.typeFilters.length"
        :type-filters="filterData.typeFilters"
      />
      <OtherFilters
        v-if="filterData.otherFilters.length"
        :other-filters="filterData.otherFilters"
      />

      <UiButton
        class="swa-mweb-filter-cta-btn"
        :text="ctaBtnText"
        :size="BtnSize.LARGE"
        :type="ctaBtnType"
        @clicked="handleCta()"
      />
    </template>
  </SliderModalContainer>
</template>

<script lang="ts" setup>
import { type ProductFilter } from '~/types/api/data/product/filter/filter.types'
import SliderModalContainer from './shared/slider-modal-container.vue'
import CategoryFilters from './shared/category-filters.vue'
import BrandFilters from './shared/brand-filters.vue'
import PriceFilters from './shared/price-filters.vue'
import ProductTypeFilters from './shared/product-type-filters.vue'
import OtherFilters from './shared/other-filters.vue'
import UiButton from '@sephora-asia/ui-button/UiButton.vue'
import { BtnType, BtnSize } from '@sephora-asia/ui-button/types'
import { isFilterParamsAvailable } from '~/utils/product-filter/filter-helpers'
import useFilterTrackingEvent from '~/composables/utils/product-listing/filter/use-product-listing-filter-tracking'

const props = withDefaults(
  defineProps<{
    filterData: ProductFilter
    isVariantFetching?: boolean
    variantCount?: number
  }>(),
  {
    isVariantFetching: false,
    variantCount: undefined,
  }
)

const store = useProductListingFiltersStore() // Destructuring breaks top-lvl refs

const { trackClearAllFilterClicked } = useFilterTrackingEvent()

const {
  clearAllSelection,
  setSelectedFilterParams,
  setToCloseAllMwebModals,
  selectedFilterParams,
  setSelectedCategory,
} = store
const { t } = useI18n()
const { getCategoryPathFromRoute, getAllQueriesFromRoute } =
  useProductListingRoutes()

let savedCategoryParamsState = getCategoryPathFromRoute()
let savedQueryState = getAllQueriesFromRoute()

/**
 * ----- States -----
 */
const toResetPriceRange = ref(false)
const isModalOpened = ref(false)
const isFetchedAfterModalOpened = ref(false) // Variants fetched after modal opened

/**
 * ----- Computed -----
 */
const ctaBtnType = computed(() => {
  return props.isVariantFetching || isFetchedAfterModalOpened.value
    ? BtnType.SECONDARY
    : BtnType.TERTIARY
})

const ctaBtnText = computed(() => {
  if (props.isVariantFetching) {
    return t('products.filters.viewResultsWithCount')
  } else if (isFetchedAfterModalOpened.value) {
    return t('products.filters.viewResultsWithCount', {
      count: props.variantCount,
    })
  } else {
    return t('dictionary.close')
  }
})

const toShowClearBtn = computed(() => {
  return isFilterParamsAvailable(selectedFilterParams) || store.selectedCategory
})

// Fixes body when modal is opened (no scrolling behind modal)
useHead({
  bodyAttrs: {
    class: computed(() =>
      isModalOpened.value ? 'mweb-product-listing-filters-fixed' : ''
    ),
  },
})

/**
 * ----- Watchers -----
 */
watch(
  () => props.variantCount,
  () => {
    if (isModalOpened.value) {
      isFetchedAfterModalOpened.value = true
    }
  }
)

/**
 * ----- Methods -----
 */
function handleOpened() {
  isModalOpened.value = true
  isFetchedAfterModalOpened.value = false // Reset this state for newly opened modal
}

async function handleCta() {
  // Reverting local states
  isModalOpened.value = false

  // Saving current query + params
  savedCategoryParamsState = toValue(store.selectedCategory)
  savedQueryState = getAllQueriesFromRoute()

  setToCloseAllMwebModals(true)
  await nextTick() // Wait for all modals to close
  setToCloseAllMwebModals(false)
}

function handleClosed() {
  // Reverting local states
  isModalOpened.value = false

  // Reverting query + params
  const isCategoryNotChanged =
    store.selectedCategory === savedCategoryParamsState
  const isQueryNotChanged =
    JSON.stringify(getAllQueriesFromRoute()) === JSON.stringify(savedQueryState)

  if (isCategoryNotChanged && isQueryNotChanged) return

  setSelectedFilterParams(savedQueryState)
  setSelectedCategory(savedCategoryParamsState)
}

function clearAll() {
  setSelectedCategory('')
  clearAllSelection()
  toResetPriceRange.value = true
  trackClearAllFilterClicked()
}
</script>

<style lang="scss" scoped>
.swa-mweb-filter-cta-btn {
  --base-gap-size: 16px;

  z-index: var(--size-z-index-popover);
  position: fixed;
  bottom: 0;

  width: calc(100% - (var(--base-gap-size) * 2));
  margin: var(--base-gap-size);
}
</style>
